import { useRef } from 'react'

import { DeviceI, InsetsI, RectI, Size } from '../types/types'
import { getDeviceClientRect, isPointWithinBounds } from '../utils/chart'

export const useFloorPosition = (
  chartSize: Size,
  chartInsets: InsetsI,
  mapImageSize: Size
) => {
  const createBinPosition = (): RectI => {
    const [width] = chartSize
    const binSize = [150, 115]
    const areaPadding = 40
    return {
      x: width - binSize[0] - areaPadding,
      y: areaPadding,
      width: binSize[0],
      height: binSize[1],
    }
  }

  const binPosition = createBinPosition()
  const binPositionRef = useRef(binPosition)
  // We create a ref of binPosition, because in the validateTrashPosition function the 'binPosition' is 'stale'.
  // (I.e the state before the devicePlacementDrawer is toggled. useRef uses the up to date value.)
  binPositionRef.current = binPosition

  const validateMapPosition = (x: number, y: number): boolean => {
    const [imageWidth, imageHeight] = mapImageSize
    return isPointWithinBounds(x, y, 0, 0, imageWidth, imageHeight)
  }

  const validateTrashPosition = (x: number, y: number): boolean => {
    return isPointWithinBounds(
      x,
      y,
      binPositionRef.current.x,
      binPositionRef.current.y,
      binPositionRef.current.x + binPositionRef.current.width,
      binPositionRef.current.y + binPositionRef.current.height
    )
  }

  const validateDevicePosition = (deviceData: DeviceI): boolean => {
    const { x, y, id } = deviceData
    const isOnMap = validateMapPosition(x, y)

    const deviceRect = getDeviceClientRect(id)
    const { x: clientX, y: clientY, width, height } = deviceRect

    // `chartInsets` may need to be in a ref,
    // `.right` might change in the future and be stale in this callback
    // but we don't use this property here
    const isInTrash = validateTrashPosition(
      clientX + width * 0.5 - chartInsets.left,
      clientY + height * 0.5 - chartInsets.top
    )
    return isOnMap && !isInTrash
  }

  return {
    binPosition: binPosition,
    validateDevicePosition,
    validateMapPosition,
  }
}
